// src/pages/FailedLogs.jsx
// The Logs tab (3.14.0: its own tab next to Purged Faxes; was in Control
// Panel). Everyone with "See the Logs tab" (Roles & Access), by the same
// rules as the fax lists: Users their own faxes, Managers and Admins their
// account, Super Admins all.
//   /portal/logs/failed-outgoing  every failed or requeued outgoing fax with
//     its failure reason and auto-retry chain (attempt, requeued by)
//   /portal/logs/failed-incoming  incoming faxes that didn't arrive
//   /portal/logs/repeated-failures  the same fax failing 2+ times to one
//     number in a day (3.57.8, owner)
//   /portal/logs/blocked-access  people blocked after 5 failed faxes to one
//     number in a day (3.57.9, owner)
// Rejected Emails, Blocked Senders and Blocked Requests are Control Panel
// sections (./RejectedLogs.jsx).
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import ResetFiltersButton, { filtersDiffer } from '../components/ResetFiltersButton';
import SortTh, { useSort } from '../components/SortTh';
import ShowSelect from '../components/ShowSelect';
import Pager, { usePager } from '../components/Pager';
import TeamFilter, { useClientTeams, teamText } from '../components/TeamFilter';
import { downloadSheet } from '../utils/api';
import EmptyState from '../components/EmptyState';
import MultiSearchSelect from '../components/MultiSearchSelect';
import { API_BASE } from '../firebase/config';
import { timeZone, tzLabel } from '../utils/timeZone';
import SectionTabs from '../components/SectionTabs';
import { useNotify } from '../components/Notifications';
import PriorityStar, { usePriorityNumbers } from '../components/PriorityStar';
import ProblemNumbers from './ProblemNumbers';
import Loader from '../components/Loader';
import Emo from '../components/Emo';
import { PILL, PILL_ON } from '../theme/tabs';
import { incomingReasonText } from '../utils/faxReasons';   // 3.62.23
import { useClientWord } from '../hooks/useClientWord';
import { useSendTimings } from '../hooks/useSendTimings';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: timeZone(), month: 'short', day: 'numeric',
    year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) + ' ' + tzLabel(new Date(iso));
}

function formatFaxNum(num) {
  if (!num) return '—';
  const n = String(num).replace(/\D/g, '');
  if (n.length === 11) return `+${n[0]} (${n.slice(1,4)}) ${n.slice(4,7)}-${n.slice(7)}`;
  if (n.length === 10) return `+1 (${n.slice(0,3)}) ${n.slice(3,6)}-${n.slice(6)}`;
  return num;
}

const REQUEUED_LABELS = {
  'auto-retry'                : { label: 'Auto-Retry',   bg: 'var(--c-e3f2fd)', color: 'var(--t-1565c0)' },
  'portal'                    : { label: 'Portal Resend', bg: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)' },
  'superseded-by-manual-send' : { label: 'Manual Send',  bg: 'var(--c-f3e5f5)', color: 'var(--t-6a1b9a)' },
};

const FAILED_LOG_DEFAULTS      = { startDate: '', endDate: '', limit: '50', team: '' };
const FAILED_INCOMING_DEFAULTS = { startDate: '', endDate: '', sender: [], reason: '', limit: '50', team: '' };

// Teams (3.19.4): shared Team filter (components/TeamFilter)

// The Logs tab (3.14.0): its own tab next to Purged Faxes, for everyone
// with "See the Logs tab", each seeing what the fax lists show them
export default function FailedLogs() {
  const { can } = useAuth();
  // 3.30.2: each part behind its own permission (Roles & Access → Logs)
  const sections = LOG_SECTIONS.filter(t => can(t.perm));
  if (!sections.length) return <div style={{ padding: 24, color: 'var(--t-607d8b)' }}>Your role doesn’t include any logs. Please ask your Admin.</div>;
  return (
    // 3.22.4: …/logs/failed-sent and …/failed-received; the old addresses still work
    <SectionTabs tabId="logs" title="📁 Logs" sections={sections} aliases={{ 'failed-outgoing': 'failed-sent', 'failed-incoming': 'failed-received' }}>
      {sub => (
        <>
          {sub === 'failed-sent' && <FailedRequeued />}
          {sub === 'failed-received' && <FailedIncoming />}
          {sub === 'repeated-failures' && <RepeatedFailures />}
          {sub === 'blocked-access' && <BlockedAccess />}
          {sub === 'problem-numbers' && <ProblemNumbers />}
        </>
      )}
    </SectionTabs>
  );
}

const LOG_SECTIONS = [
  { id: 'failed-sent', label: 'Failed Sent', icon: '📤', perm: 'logs.failedSent' },
  { id: 'failed-received', label: 'Failed Received', icon: '📥', perm: 'logs.failedReceived' },
  { id: 'repeated-failures', label: 'Repeated failures', icon: '🔁', perm: 'logs.repeatedFailures' },
  { id: 'blocked-access', label: 'Blocked access', icon: '🚫', perm: 'logs.blockedAccess' },
  { id: 'problem-numbers', label: 'Problem numbers', icon: '⚠️', perm: 'logs.problemNumbers' },   // 3.60.0
];

// "Email block alerts to" (3.57.18, owner): the client's Admins pick which of
// them get the block alert emails; none picked = all of them. Shown to the
// client's Admins, and to Super Admins with a client picked.
// 3.57.19 (owner): one short line with a drop-down of tick boxes; each tick
// saves at once.
function BlockAlertTo() {
  const { getToken } = useAuth();
  const { notify } = useNotify();
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const call = async (method, body) => {
    const r = await fetch(`${API_BASE}/portal/logs/blocked-access/alert-to`, { method, headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' }, ...(body ? { body: JSON.stringify(body) } : {}) });
    const j = await r.json(); if (!r.ok) throw new Error(j.error || 'That didn\'t work'); return j;
  };
  useEffect(() => { call('GET').then(setS).catch(() => setS(null)); }, [getToken]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return undefined;
    const away = e => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const esc = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);
  if (!s?.editable || !s.admins?.length) return null;
  const picked = s.picked || [];
  const nameOf = e => { const a = s.admins.find(x => x.email === e); return a?.name || e; };
  const summary = !picked.length ? 'All Admins' : picked.length === 1 ? nameOf(picked[0]) : `${nameOf(picked[0])} +${picked.length - 1} more`;
  const toggle = async e => {
    const next = picked.includes(e) ? picked.filter(x => x !== e) : [...picked, e];
    setBusy(true);
    try { const j = await call('PUT', { emails: next }); setS(x => ({ ...x, picked: j.picked })); }
    catch (err) { notify({ tone: 'error', title: 'Couldn\'t save it', detail: err.message }); }
    setBusy(false);
  };
  return (
    <div ref={boxRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap', fontSize: 13 }}>
      <span style={{ fontWeight: 600, color: 'var(--t-37474f)' }}>Email block alerts to:</span>
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open}
        style={{ ...S.filterInput, background: 'var(--c-ffffff)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 170, justifyContent: 'space-between', textAlign: 'left' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>{summary}</span>
        <span aria-hidden="true" style={{ fontSize: 10, color: 'var(--t-78909c)' }}>▼</span>
      </button>
      <span style={{ fontSize: 12, color: 'var(--t-78909c)' }}>Fax Portal support is always copied.</span>
      {open && (
        <div role="listbox" aria-multiselectable="true" aria-label="Admins who get block alerts"
          style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 20, minWidth: 260, maxHeight: 280, overflowY: 'auto',
            background: 'var(--c-ffffff)', border: '1px solid var(--c-cfd8dc)', borderRadius: 10, boxShadow: '0 10px 30px -10px rgba(0,0,0,.25)', padding: 6 }}>
          <div style={{ padding: '6px 8px', fontSize: 12, color: 'var(--t-78909c)' }}>None ticked = every Admin gets them</div>
          {s.admins.map(a => (
            <label key={a.email} role="option" aria-selected={picked.includes(a.email)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 6, cursor: busy ? 'wait' : 'pointer' }}>
              <input type="checkbox" checked={picked.includes(a.email)} onChange={() => toggle(a.email)} disabled={busy} />
              <span>{a.name || a.email}{a.name && <span style={{ display: 'block', fontSize: 11, color: 'var(--t-78909c)' }}>{a.email}</span>}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Blocked access (3.57.9, owner): people whose access was blocked after 5
// failed faxes to one number in a day, and who unblocked them. Unblocking
// is in Users → Blocked. By hierarchy: Super Admins all clients, Admins
// their client, Managers their teams' Users.
function BlockedAccess() {
  const tm = useSendTimings();   // 3.75.0: the block's count and hours (Sending Timings)
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const { getToken, can } = useAuth();
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  useEffect(() => {
    let live = true;
    setRows(null); setError('');
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/portal/logs/blocked-access?days=${days}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || "Couldn't load blocked access.");
        if (live) setRows(j.data || []);
      } catch (e) { if (live) { setError(e.message); setRows([]); } }
    })();
    return () => { live = false; };
  }, [days, getToken]);
  const who = r => r.name ? `${r.name} (${r.email})` : r.email;
  const heldText = r => !r.held?.length ? '' : ` · ${r.held.length} held fax${r.held.length === 1 ? '' : 'es'}${r.heldResult === 'sent' ? ' sent' : r.heldResult === 'dropped' ? ' dropped' : ''}`;
  // 3.57.18: blocks last 24 hours; "automatic (24 hours)" = restored by itself
  const statusText = r => (r.active ? `Blocked until ${formatDate(r.until)}`
    : String(r.unblockedBy || '').startsWith('automatic') ? `Restored automatically ${formatDate(r.unblockedAt)}`
    : `Unblocked ${formatDate(r.unblockedAt)}${r.unblockedBy ? ` by ${r.unblockedBy}` : ''}`) + heldText(r);
  const via = a => (a.source === 'email' ? 'by email' : a.resend ? 'Resend' : 'from the portal');
  async function download() {
    await downloadSheet('Blocked access', [
      { header: 'Blocked', get: r => formatDate(r.at), width: 22 },
      { header: clientWord, get: r => r.accountName || '', width: 18 },
      { header: 'Person', get: who, width: 32 },
      { header: 'Fax number', get: r => formatFaxNum(r.number), width: 18 },
      { header: 'Failed faxes', get: r => r.count, width: 12 },
      { header: 'Last reason', get: r => r.reasonText, width: 34 },
      { header: 'Status', get: statusText, width: 40 },
    ], rows || [], getToken);
  }
  return (
    <div>
      <div style={S.filterBar}>
        <div style={{ ...S.filterRow, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[[7, '7 days'], [30, '30 days'], [90, '90 days']].map(([d, l]) => (
              <button key={d} onClick={() => setDays(d)} style={{ ...PILL, ...(days === d ? PILL_ON : {}) }} aria-pressed={days === d}>{l}</button>
            ))}
          </div>
          {can('fax.reports') && <button onClick={download} disabled={!rows?.length} style={{ ...S.downloadBtn, opacity: rows?.length ? 1 : 0.5 }} title="Download what's shown as an Excel file"><Emo e="⬇" /> Excel</button>}
        </div>
        <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--t-546e7a)' }}>
          Users and Managers are blocked for {tm.blockFor} when the same file fails {tm.blockLimit} times to one number in a day (from the portal or by email; only real problems on the receiver's side, not busy lines; automatic retries aren't counted). Access comes back by itself after that, or earlier when an Admin unblocks it in Users → Blocked. Click a row to see each try.
        </p>
        <BlockAlertTo />
      </div>
      <div style={S.resultCount}>{rows == null ? 'Loading...' : `${rows.length} block${rows.length === 1 ? '' : 's'}`}</div>
      {error && <div style={S.errorBanner}>{error}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr style={S.thead}>
              {['Blocked', 'Person', 'Fax number', 'Failed faxes', 'Last reason', 'Status'].map(h => <th key={h} style={S.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows == null ? (
              <tr><td colSpan={6} style={S.emptyCell}><Loader /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} style={S.emptyCell}><EmptyState icon="🚫" emptyText="Nobody was blocked in this period." /></td></tr>
            ) : rows.map((r, i) => [
              <tr key={r.id} onClick={() => setOpen(open === r.id ? null : r.id)} style={{ ...S.tr, cursor: 'pointer', background: i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)' }}>
                <td style={{ ...S.td, fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(r.at)}{r.accountName && <div style={{ fontSize: 11, color: 'var(--t-78909c)' }}>{r.accountName}</div>}</td>
                <td style={S.td}>{r.name || r.email}{r.name && <div style={{ fontSize: 11, color: 'var(--t-78909c)' }}>{r.email}</div>}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{formatFaxNum(r.number)}</td>
                <td style={{ ...S.td, fontWeight: 700, color: 'var(--t-b71c1c)' }}>{r.count}</td>
                <td style={S.td}><span style={S.reasonCode}>{r.reasonText}</span></td>
                <td style={{ ...S.td, fontSize: 12, color: r.active ? 'var(--t-b71c1c)' : 'var(--t-2e7d32)' }}>{r.active ? '🚫 ' : '✅ '}{statusText(r)}</td>
              </tr>,
              open === r.id && (
                <tr key={`${r.id}:tries`}>
                  <td colSpan={6} style={{ ...S.td, background: 'var(--c-f1f8f7)' }}>
                    {r.held?.length > 0 && <div style={{ padding: '3px 0', fontWeight: 600 }}>Held when blocked: {r.held.map(h => h.fileName || 'fax').join(', ')}</div>}
                    {r.attempts.map((a, n) => (
                      <div key={a.faxId || n} style={{ padding: '3px 0' }}>
                        <strong>Try {n + 1}</strong> · {formatDate(a.at)} · {a.fileName || '—'} · {via(a)} · <span style={{ color: 'var(--t-b71c1c)' }}>{a.reasonText}</span>
                      </div>
                    ))}
                  </td>
                </tr>
              ),
            ])}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Repeated failures (3.57.8, owner): the same fax (sender, number, file)
// failing 2 or more times in a day, from the portal or by email (automatic
// retries not counted), and whether it got through later. Each person sees
// what the fax lists show them. No costs.
function RepeatedFailures() {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const { getToken, can } = useAuth();
  const [days, setDays] = useState(1);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  useEffect(() => {
    let live = true;
    setRows(null); setError('');
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/portal/logs/repeated-failures?days=${days}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || "Couldn't load repeated failures.");
        if (live) setRows(j.data || []);
      } catch (e) { if (live) { setError(e.message); setRows([]); } }
    })();
    return () => { live = false; };
  }, [days, getToken]);
  const via = r => r.sources.map(x => (x === 'email' ? 'by email' : 'from the portal')).join(', ');
  const laterText = r => !r.later ? 'No' : r.later.sameNumber ? `Yes, ${formatDate(r.later.at)}` : `Yes, to ${formatFaxNum(r.later.number)} (${formatDate(r.later.at)})`;
  const who = r => r.fromName ? `${r.fromName} (${r.fromEmail})` : r.fromEmail;
  async function download() {
    await downloadSheet('Repeated failures', [
      { header: 'Day', get: r => r.day, width: 12 },
      { header: clientWord, get: r => r.accountName || '', width: 18 },
      { header: 'Sender', get: who, width: 32 },
      { header: 'Fax number', get: r => formatFaxNum(r.number), width: 18 },
      { header: 'File', get: r => r.fileName || '', width: 30 },
      { header: 'Failed tries', get: r => r.failed, width: 12 },
      { header: 'Sent', get: via, width: 18 },
      { header: 'Last reason', get: r => r.lastReasonText, width: 34 },
      { header: 'First try', get: r => formatDate(r.firstAt), width: 22 },
      { header: 'Last try', get: r => formatDate(r.lastAt), width: 22 },
      { header: 'Got through later', get: laterText, width: 34 },
    ], rows || [], getToken);
  }
  return (
    <div>
      <div style={S.filterBar}>
        <div style={{ ...S.filterRow, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[[1, 'Today'], [7, '7 days'], [30, '30 days']].map(([d, l]) => (
              <button key={d} onClick={() => setDays(d)} style={{ ...PILL, ...(days === d ? PILL_ON : {}) }} aria-pressed={days === d}>{l}</button>
            ))}
          </div>
          {can('fax.reports') && <button onClick={download} disabled={!rows?.length} style={{ ...S.downloadBtn, opacity: rows?.length ? 1 : 0.5 }} title="Download what's shown as an Excel file"><Emo e="⬇" /> Excel</button>}
        </div>
        <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--t-546e7a)' }}>
          The same fax (sender, fax number and file) that failed 2 or more times in a day, sent from the portal or by email. Automatic retries aren't counted. Click a row to see each try.
        </p>
      </div>
      <div style={S.resultCount}>{rows == null ? 'Loading...' : `${rows.length} repeated failure${rows.length === 1 ? '' : 's'}`}</div>
      {error && <div style={S.errorBanner}>{error}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr style={S.thead}>
              {['Day', 'Sender', 'Fax number', 'File', 'Failed tries', 'Last reason', 'First → last try', 'Got through later?'].map(h => <th key={h} style={S.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows == null ? (
              <tr><td colSpan={8} style={S.emptyCell}><Loader /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} style={S.emptyCell}><EmptyState icon="🔁" emptyText="No repeated failures in this period." /></td></tr>
            ) : rows.map((r, i) => [
              <tr key={r.key} onClick={() => setOpen(open === r.key ? null : r.key)} style={{ ...S.tr, cursor: 'pointer', background: i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)' }}>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{r.day}{r.accountName && <div style={{ fontSize: 11, color: 'var(--t-78909c)' }}>{r.accountName}</div>}</td>
                <td style={S.td}>{r.fromName || r.fromEmail}{r.fromName && <div style={{ fontSize: 11, color: 'var(--t-78909c)' }}>{r.fromEmail}</div>}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{formatFaxNum(r.number)}</td>
                <td style={{ ...S.td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.fileName || ''}>{r.fileName || '—'}</td>
                <td style={{ ...S.td, fontWeight: 700, color: 'var(--t-b71c1c)' }}>{r.failed}<div style={{ fontSize: 11, fontWeight: 400, color: 'var(--t-78909c)' }}>{via(r)}</div></td>
                <td style={S.td}><span style={S.reasonCode}>{r.lastReasonText}</span></td>
                <td style={{ ...S.td, fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(r.firstAt)}<br />→ {formatDate(r.lastAt)}</td>
                <td style={{ ...S.td, fontSize: 12, color: r.later ? 'var(--t-2e7d32)' : 'var(--t-b71c1c)' }}>{r.later ? '✅ ' : '❌ '}{laterText(r)}</td>
              </tr>,
              open === r.key && (
                <tr key={`${r.key}:tries`}>
                  <td colSpan={8} style={{ ...S.td, background: 'var(--c-f1f8f7)' }}>
                    {r.attempts.map((a, n) => (
                      <div key={a.faxId} style={{ padding: '3px 0' }}>
                        <strong>Try {n + 1}</strong> · {formatDate(a.at)} · {a.source === 'email' ? 'by email' : a.resend ? 'Resend' : 'from the portal'} · <span style={{ color: 'var(--t-b71c1c)' }}>{a.reasonText}</span>
                      </div>
                    ))}
                  </td>
                </tr>
              ),
            ])}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Why a failed fax was or wasn't retried (3.22.3, saved on the fax by the
// backend: retryNote { code, … }); older faxes have only retryCancelled
function retryText(r, canStar) {
  const n = r.retryNote;
  const at = iso => new Date(iso).toLocaleString('en-US', { timeZone: timeZone(), hour: 'numeric', minute: '2-digit', month: 'short', day: 'numeric' }) + ' ' + tzLabel(new Date(iso));
  if (!n) return r.retryCancelled ? `Retry cancelled: ${r.retryCancelled}` : null;
  switch (n.code) {
    case 'scheduled': if (!r.requeued && r.retryCancelled) return `No retry: it was cancelled${r.retryCancelled === 'admin' ? ' by Fax Portal support' : r.retryCancelled === 'user' ? ' by hand' : ''}. Resend it when it should go out.`;
      // 3.78.0: longer retries (no fixed count) and High failure congestion numbers (their good hours)
      { const of = n.of ? ` of ${n.of}` : '';
        const kind = n.congested ? ', High failure congestion number' : n.priority ? ', ★ priority number' : '';
        const until = n.until ? ` Retries go on until ${at(n.until)} at the latest.` : '';
        return r.requeued ? `Retried (try ${n.nextAttempt}${of})${kind ? ` ·${kind.slice(1)}` : ''}`
          : `Will retry at ${at(n.fireAt)} (try ${n.nextAttempt}${of}${kind}).${n.congested ? ` Retries to this number go out at the times it usually takes faxes${n.hours ? ` (${n.hours})` : ''}.` : ' Retries go out in your company’s office hours (Settings → Office Hours).'}${until}`; }
    case 'number-failing': return `No retry: retries are paused for ${formatFaxNum(r.toNumber)} because ${n.failures} faxes to it failed in the last 24 hours.${canStar ? ' If it must keep being tried, mark the number ★ priority, then Resend.' : ''}`;
    // 3.60.2: on Problem numbers as Not a fax line (Logs → Problem numbers)
    case 'not-fax-line': return `No retry: ${formatFaxNum(r.toNumber)} is on Problem numbers as Not a fax line (no fax to it got through in the last 30 days${n.source === 'support' ? ', set by Fax Portal support' : n.source === 'client' ? ', set by your Admin' : ''}). It may be a voice line: confirm the fax number with the receiver.`;
    case 'not-retryable': return 'No retry: this kind of failure isn\'t retried automatically (it usually means the number can\'t take faxes).';
    case 'last-attempt': return n.ended ? `No more retries: it was tried until the end of the next business day (${n.attempts} tries in all). Resend it if it's still needed.` : `No more retries: it was sent and retried (${n.attempts} attempts in all). Resend it if it's still needed.`;
    case 'retries-off': return 'No retry: automatic retries are switched off for your company.';
    case 'no-file': return 'No retry: the fax file is no longer kept.';
    case 'superseded': return 'No retry: it was sent again by hand.';
    case 'blocked': return 'No retry: the number is on your company’s blocked numbers (Settings).';
    case 'send-error': return 'No retry: the fax service refused the retry when it was due. Resend it when it should go out.';   // 3.62.14
    case 'send-unknown': return 'The retry may have gone out: the fax service didn\'t confirm it. Check the Outbox before sending it again.';   // 3.62.14
    default: return 'No retry: it couldn\'t be scheduled.';
  }
}

function FailedRequeued() {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const { getToken, can } = useAuth();
  const { notify, confirm } = useNotify();
  const [resending, setResending] = useState(null);
  const [priority, reloadPriority] = usePriorityNumbers();   // ★ priority numbers (3.22.3)
  // Resend from here (3.22.3): a final failure, for roles that may resend
  async function resend(r) {
    const ok = await confirm({ title: 'Resend this fax?', message: `It will be sent again to ${formatFaxNum(r.toNumber)}.`, confirmLabel: 'Resend' });
    if (!ok) return;
    setResending(r.faxId);
    try {
      const res = await fetch(`${API_BASE}/portal/resend/${r.faxId}`, { method: 'POST', headers: { 'Authorization': `Bearer ${await getToken()}`, 'Content-Type': 'application/json' } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Resend failed');
      if (data.waiting) notify({ bell: 'fax', tone: 'success', title: 'Fax waiting for the line', detail: 'That number is busy with another fax. It goes out by itself as soon as the line is free (see Outbox).' });
      else notify({ bell: 'fax', tone: 'success', title: 'Fax queued to send again', detail: `New reference ID: ${data.faxId}` });
      loadLogs();
    } catch (e) { notify({ tone: 'error', title: 'Resend failed', detail: e.message }); }
    setResending(null);
  }
  const clientTeams           = useClientTeams();
  const [data, setData]       = useState([]);
  const [summary, setSummary] = useState(null);
  const [retryStats, setRetryStats] = useState(null);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [filters, setFilters] = useState(FAILED_LOG_DEFAULTS);
  const [applied, setApplied] = useState(FAILED_LOG_DEFAULTS); // dates of the last search
  const [statusFilter, setStatusFilter] = useState('all');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLogs(); }, []);

  async function loadLogs(f = filters) {
    setLoading(true);
    setError('');
    try {
      const token  = await getToken();
      const params = new URLSearchParams();
      if (f.startDate) params.append('startDate', f.startDate);
      if (f.endDate)   params.append('endDate',   f.endDate);
      params.append('limit', 'all');   // every row; shown page by page (3.26.0)
      if (f.team) params.append('team', f.team);

      const res  = await fetch(`${API_BASE}/portal/logs/failed?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load logs');
      setData(json.data || []);
      setSummary(json.summary || null);
      setRetryStats(json.retryStats || null);
      setTotal(json.total || 0);
      setApplied(f);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  function handleFilterChange(key, value) {
    setFilters(prev => ({ ...prev, [key]: value }));
  }

  function resetFilters() {
    setFilters(FAILED_LOG_DEFAULTS);
    setStatusFilter('all');
    loadLogs(FAILED_LOG_DEFAULTS);
  }

  // Dates count once searched; Show filters the list as it changes
  const resultFiltered = filtersDiffer(applied, FAILED_LOG_DEFAULTS) || statusFilter !== 'all';

  const topReasons = summary
    ? Object.entries(summary.reasonCounts || {}).sort((a, b) => b[1] - a[1]).slice(0, 8)
    : [];

  const visibleRows = data.filter(r => {
    if (statusFilter === 'failed') return r.status === 'failed';
    if (statusFilter === 'auto')   return r.autoRetry || r.requeuedBy === 'auto-retry';
    return true;
  });

  // Sorting and Excel (3.16.0); Team (3.19.4)
  const showTeam = clientTeams.length > 0 || data.some(r => (r.teamNames || []).length);
  const { sorted: sortedRows, sort, onSort } = useSort(visibleRows, {
    team: teamText, date: r => r.sentAt || r.updatedAt || '', from: r => r.fromEmail, to: r => String(r.toNumber || '').replace(/\D/g, ''),
    file: r => r.fileName, pages: r => Number(r.pages) || 0, attempt: r => Number(r.tries || r.attempt) || 0,
    reason: r => r.failureReason, status: r => r.status, requeued: r => r.requeuedBy || (r.requeued ? 'requeued' : ''),
  });
  const { pageRows, pager } = usePager(sortedRows, filters.limit);   // page by page (3.26.0)
  async function downloadOutgoing() {
    try {
      await downloadSheet('Failed Sent', [
        { header: 'Date & Time', get: r => formatDate(r.sentAt || r.updatedAt), width: 24 },
        { header: clientWord, get: r => r.accountName || '', width: 16 },
        ...(showTeam ? [{ header: 'Team', get: teamText, width: 18 }] : []),
        { header: 'Sent By', get: r => r.fromEmail || '', width: 28 },
        { header: 'Sent To', get: r => formatFaxNum(r.toNumber), width: 18 },
        { header: 'File', get: r => r.fileName || '', width: 30 },
        { header: 'Pages', get: r => r.pages ?? '', width: 7 },
        { header: 'Tries', get: r => `${r.tries || r.attempt}`, width: 10 },
        { header: 'Failure Reason', get: r => r.failureReason || '', width: 28 },
        { header: 'Status', get: r => (r.status || '').toUpperCase(), width: 12 },
        { header: 'Requeued Via', get: r => (REQUEUED_LABELS[r.requeuedBy]?.label || r.requeuedBy || (r.requeued ? 'Requeued' : '')), width: 16 },
      ], sortedRows, getToken);
    } catch (e) { setError(e.message); }
  }

  return (
    <div>
      {/* Summary Cards */}
      {summary && (
        <div style={S.cards}>
          <div style={S.card}>
            <div style={S.cardValue}>{total}</div>
            <div style={S.cardLabel}>Total Records</div>
          </div>
          <div style={S.card}>
            <div style={{ ...S.cardValue, color: 'var(--t-b71c1c)' }}>{summary.failed}</div>
            <div style={S.cardLabel}>Failed</div>
          </div>
          <div style={S.card}>
            <div style={{ ...S.cardValue, color: 'var(--t-e65100)' }}>{summary.requeued}</div>
            <div style={S.cardLabel}>Requeued</div>
          </div>
          <div style={S.card}>
            <div style={{ ...S.cardValue, color: 'var(--t-1565c0)' }}>{summary.autoRetries}</div>
            <div style={S.cardLabel}>Auto-Retry Attempts</div>
          </div>
        </div>
      )}

      {/* Retry effectiveness */}
      {retryStats && (
        retryStats.chains > 0 ? (
          <div style={S.effectStrip}>
            <span style={S.effectTitle}><Emo e="🔁" /> Auto-Retry Effectiveness</span>
            <span style={S.effectItem}><strong>{retryStats.chains}</strong> chains retried</span>
            <span style={{ ...S.effectItem, color: 'var(--t-2e7d32)' }}>
              <strong>{retryStats.recovered}</strong> recovered ({retryStats.recoveryRate}%)
            </span>
            <span style={{ ...S.effectItem, color: 'var(--t-b71c1c)' }}><strong>{retryStats.exhausted}</strong> exhausted</span>
            {retryStats.inFlight > 0 && (
              <span style={{ ...S.effectItem, color: 'var(--t-1565c0)' }}><strong>{retryStats.inFlight}</strong> in flight</span>
            )}
            {retryStats.cancelled > 0 && (
              <span style={{ ...S.effectItem, color: 'var(--t-6a1b9a)' }}><strong>{retryStats.cancelled}</strong> cancelled</span>
            )}
          </div>
        ) : (
          <div style={{ ...S.effectStrip, color: 'var(--t-90a4ae)' }}>
            <Emo e="🔁" /> No auto-retry activity yet for this range.
          </div>
        )
      )}

      {/* Failure reason chips */}
      {topReasons.length > 0 && (
        <div style={S.chipRow}>
          {topReasons.map(([reason, count]) => (
            <span key={reason} style={S.chip}>
              <code style={S.chipCode}>{reason}</code> × {count}
            </span>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={S.filterBar}>
        <div style={S.filterRow}>
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>From Date</label>
            <input type="date" value={filters.startDate}
              onChange={e => handleFilterChange('startDate', e.target.value)} style={S.filterInput} />
          </div>
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>To Date</label>
            <input type="date" value={filters.endDate}
              onChange={e => handleFilterChange('endDate', e.target.value)} style={S.filterInput} />
          </div>
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Records</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={S.filterInput}>
              <option value="all">All records</option>
              <option value="failed">Failed only</option>
              <option value="auto">Auto-retries only</option>
            </select>
          </div>
          <TeamFilter teams={clientTeams} value={filters.team} onChange={v => handleFilterChange('team', v)}
            groupStyle={S.filterGroup} labelStyle={S.filterLabel} style={S.filterInput} />
          <ShowSelect value={filters.limit} onChange={v => handleFilterChange('limit', v)}
            groupStyle={S.filterGroup} labelStyle={S.filterLabel} style={S.filterInput} />
          <div style={{ ...S.filterGroup, justifyContent: 'flex-end', alignSelf: 'flex-end' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => loadLogs()} style={S.searchBtn}><Emo e="🔍" /> Search</button>
              <ResetFiltersButton
                dirty={filtersDiffer(filters, FAILED_LOG_DEFAULTS) || statusFilter !== 'all'}
                onReset={resetFilters} />
              {can('fax.reports') && <button onClick={downloadOutgoing} disabled={!sortedRows.length} style={{ ...S.downloadBtn, opacity: sortedRows.length ? 1 : 0.5 }} title="Download what's shown as an Excel file"><Emo e="⬇" /> Excel</button>}
            </div>
          </div>
        </div>
      </div>

      <div style={S.resultCount}>
        {loading ? 'Loading...' : `${visibleRows.length} of ${total} record${total !== 1 ? 's' : ''} shown`}
      </div>

      {error && <div style={S.errorBanner}>{error}</div>}

      {/* Table */}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr style={S.thead}>
              <SortTh k="date" label="Date & Time" sort={sort} onSort={onSort} style={S.th} />
              {showTeam && <SortTh k="team" label="Team" sort={sort} onSort={onSort} style={S.th} />}
              <SortTh k="from" label="Sent By" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="to" label="Sent To" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="file" label="File" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="pages" label="Pages" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="attempt" label="Tries" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="reason" label="Failure Reason" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="status" label="Status" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="requeued" label="Requeued Via" sort={sort} onSort={onSort} style={S.th} />
              <th style={S.th}>Retry</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} style={S.emptyCell}><LoadingSpinner /></td></tr>
            ) : sortedRows.length === 0 ? (
              <tr><td colSpan={11} style={S.emptyCell}>
                <EmptyState icon="📜" filtered={resultFiltered} onClear={resetFilters}
                  filteredText="No failed or requeued sent faxes match these filters."
                  emptyText="No failed or requeued sent faxes yet." />
              </td></tr>
            ) : pageRows.map((r, i) => {
              const rq = REQUEUED_LABELS[r.requeuedBy] ||
                (r.requeued ? { label: r.requeuedBy || 'Requeued', bg: 'var(--c-fff8e1)', color: 'var(--t-e65100)' } : null);
              return (
                <tr key={r.faxId} style={{ ...S.tr, background: i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)' }}>
                  <td style={{ ...S.td, fontSize: 12, color: 'var(--t-546e7a)', whiteSpace: 'nowrap' }}
                      title={r.updatedAt ? `Last updated: ${formatDate(r.updatedAt)}` : ''}>
                    {formatDate(r.sentAt || r.updatedAt)}
                    {r.accountName && <div style={{ fontSize: 11, color: 'var(--t-78909c)' }}>{r.accountName}</div>}
                  </td>
                  {showTeam && <td style={S.td}>{teamText(r) || '—'}</td>}
                  <td style={{ ...S.td, color: 'var(--t-00695c)', fontWeight: 500 }}>{r.fromEmail || '—'}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{formatFaxNum(r.toNumber)}<PriorityStar fax={r} map={priority} onChanged={reloadPriority} /></td>
                  <td style={{ ...S.td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={r.fileName || ''}>{r.fileName || '—'}</td>
                  <td style={S.td}>{r.pages ?? '—'}</td>
                  <td style={S.td} title={r.tries > 1 ? `${r.tries} tries in all; each one is in the fax's details` : undefined}>
                    {/* 3.78.0 (owner): one row per document, with how many tries it took */}
                    {r.tries || r.attempt}
                    {r.tries > 1 && (
                      <span style={{ ...S.badge, background: 'var(--c-e3f2fd)', color: 'var(--t-1565c0)', marginLeft: 6 }}>tries</span>
                    )}
                  </td>
                  <td style={S.td}>
                    {r.failureReason
                      ? <code style={S.reasonCode}>{r.failureReason}</code>
                      : '—'}
                  </td>
                  <td style={S.td}>
                    <span style={{
                      ...S.badge,
                      background: r.status === 'failed' ? 'var(--c-fce4ec)' : r.status === 'delivered' ? 'var(--c-e8f5e9)' : 'var(--c-fff8e1)',
                      color: r.status === 'failed' ? 'var(--t-b71c1c)' : r.status === 'delivered' ? 'var(--t-2e7d32)' : 'var(--t-e65100)',
                    }}>{(r.status || '—').toUpperCase()}</span>
                  </td>
                  <td style={S.td}>
                    {rq
                      ? <span style={{ ...S.badge, background: rq.bg, color: rq.color }}>{rq.label}</span>
                      : '—'}
                  </td>
                  <td style={{ ...S.td, minWidth: 200, maxWidth: 320, fontSize: 12, color: r.retryNote?.code === 'scheduled' && !r.retryCancelled ? 'var(--t-2e7d32)' : 'var(--t-e65100)' }}>
                    {retryText(r, can('fax.priority')) || '—'}
                    {r.status === 'failed' && !r.requeued && (r.retryNote?.code !== 'scheduled' || r.retryCancelled) && can('fax.resend') && (
                      <div style={{ marginTop: 4 }}>
                        <button onClick={() => resend(r)} disabled={resending === r.faxId} style={S.resendBtn}>{resending === r.faxId ? 'Resending…' : <><Emo e="↻" /> Resend</>}</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!loading && <Pager {...pager} label="faxes" />}
    </div>
  );
}

// ── Failed Incoming: faxes sent to our line that failed mid-receive ──
// No document is stored for these, but each counts toward incoming usage.
// Pages are shown as billed (3.17.0): 0 transferred counts as the account's
// billing setting (10). The pages that actually arrived aren't shown to
// anyone (3.18.1).
function FailedIncoming() {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const { getToken, can }     = useAuth();
  const clientTeams           = useClientTeams();
  const [data, setData]       = useState([]);
  const [summary, setSummary] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [filters, setFilters] = useState(FAILED_INCOMING_DEFAULTS);
  const [applied, setApplied] = useState(FAILED_INCOMING_DEFAULTS); // filters of the last search

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadFailedIncoming(); }, []);

  async function loadFailedIncoming(f = filters) {
    setLoading(true);
    setError('');
    try {
      const token  = await getToken();
      const params = new URLSearchParams();
      // Picked senders are sent comma-separated; empty filters are left out
      Object.entries(f).forEach(([k, v]) => {
        const val = Array.isArray(v) ? v.join(',') : v;
        if (val) params.append(k, val);
      });
      params.set('limit', 'all');   // every row; shown page by page (3.26.0)
      const res  = await fetch(`${API_BASE}/portal/logs/receives?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json.data || []);
      setSummary(json.summary || null);
      setReasons(json.reasons || []);
      setTotal(json.total || 0);
      setApplied(f);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  function setFilter(key, value) {
    setFilters(prev => ({ ...prev, [key]: value }));
  }

  function resetFilters() {
    setFilters(FAILED_INCOMING_DEFAULTS);
    loadFailedIncoming(FAILED_INCOMING_DEFAULTS);
  }

  // Sorting and Excel (3.16.0); Team (3.19.4)
  const showTeam = clientTeams.length > 0 || data.some(r => (r.teamNames || []).length);
  const { sorted: sortedRows, sort, onSort } = useSort(data, {
    team: teamText, date: r => r.failedAt || '', from: r => String(r.fromNumber || '').replace(/\D/g, ''), to: r => String(r.toNumber || '').replace(/\D/g, ''),
    pages: r => Number(r.pages) || 0, reason: r => r.failureReason,
  });
  const { pageRows, pager } = usePager(sortedRows, filters.limit);   // page by page (3.26.0)
  async function downloadIncoming() {
    try {
      await downloadSheet('Failed Received', [
        { header: 'Failed At', get: r => formatDate(r.failedAt), width: 24 },
        { header: clientWord, get: r => r.accountName || '', width: 16 },
        ...(showTeam ? [{ header: 'Team', get: teamText, width: 18 }] : []),
        { header: 'From (Sender)', get: r => formatFaxNum(r.fromNumber), width: 18 },
        { header: 'To (Our Line)', get: r => formatFaxNum(r.toNumber), width: 18 },
        { header: 'Pages', get: r => r.pages ?? 0, width: 8 },
        { header: 'Reason', get: r => incomingReasonText(r.failureReason), width: 40 },
        { header: 'Carrier code', get: r => r.failureReason || '', width: 30 },
      ], sortedRows, getToken);
    } catch (e) { setError(e.message); }
  }

  const topReasons = summary
    ? Object.entries(summary.reasonCounts || {}).sort((a, b) => b[1] - a[1]).slice(0, 8)
    : [];

  return (
    <div>
      {summary && (
        <div style={S.cards}>
          <div style={S.card}>
            <div style={S.cardValue}>{total}</div>
            <div style={S.cardLabel}>Failed Received</div>
          </div>
          <div style={S.card}>
            <div style={S.cardValue}>{summary.uniqueSenders}</div>
            <div style={S.cardLabel}>Unique Senders</div>
          </div>
          <div style={S.card}>
            <div style={{ ...S.cardValue, color: 'var(--t-e65100)' }}>{summary.pagesBilled ?? 0}</div>
            <div style={S.cardLabel}>Pages</div>
          </div>
        </div>
      )}

      {topReasons.length > 0 && (
        <div style={S.chipRow}>
          {topReasons.map(([rsn, count]) => (
            <span key={rsn} style={S.chip}>
              <code style={S.chipCode}>{rsn}</code> × {count}
            </span>
          ))}
        </div>
      )}

      {summary?.repeatSenders?.length > 0 && (
        <div style={S.chipRow}>
          <span style={{ ...S.chip, background: 'var(--c-fff8e1)', border: '1px solid var(--c-f9a825)', color: 'var(--t-e65100)', fontWeight: 600 }}>
            Repeat failing senders:
          </span>
          {summary.repeatSenders.map(s => (
            <span key={s.sender} style={S.chip}>{formatFaxNum(s.sender)} × {s.count}</span>
          ))}
        </div>
      )}

      <div style={S.filterBar}>
        <div style={S.filterRow}>
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>From Date</label>
            <input type="date" value={filters.startDate}
              onChange={e => setFilter('startDate', e.target.value)} style={S.filterInput} />
          </div>
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>To Date</label>
            <input type="date" value={filters.endDate}
              onChange={e => setFilter('endDate', e.target.value)} style={S.filterInput} />
          </div>
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Sender Fax #</label>
            <MultiSearchSelect suggestType="failedIncoming" field="fromNumber"
              values={filters.sender} onChange={v => setFilter('sender', v)}
              placeholder="e.g. 8035676987" />
          </div>
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Reason</label>
            <select value={filters.reason} onChange={e => setFilter('reason', e.target.value)} style={S.filterInput}>
              <option value="">All reasons</option>
              {reasons.map(rsn => <option key={rsn} value={rsn}>{rsn}</option>)}
            </select>
          </div>
          <TeamFilter teams={clientTeams} value={filters.team} onChange={v => setFilter('team', v)}
            groupStyle={S.filterGroup} labelStyle={S.filterLabel} style={S.filterInput} />
          <ShowSelect value={filters.limit} onChange={v => setFilter('limit', v)}
            groupStyle={S.filterGroup} labelStyle={S.filterLabel} style={S.filterInput} />
          <div style={{ ...S.filterGroup, justifyContent: 'flex-end', alignSelf: 'flex-end' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => loadFailedIncoming()} style={S.searchBtn}><Emo e="🔍" /> Search</button>
              <ResetFiltersButton dirty={filtersDiffer(filters, FAILED_INCOMING_DEFAULTS)}
                onReset={resetFilters} />
              {can('fax.reports') && <button onClick={downloadIncoming} disabled={!sortedRows.length} style={{ ...S.downloadBtn, opacity: sortedRows.length ? 1 : 0.5 }} title="Download what's shown as an Excel file"><Emo e="⬇" /> Excel</button>}
            </div>
          </div>
        </div>
      </div>

      <div style={S.resultCount}>
        {loading ? 'Loading...' : `${data.length} of ${total} record${total !== 1 ? 's' : ''} shown`}
      </div>

      {error && <div style={S.errorBanner}>{error}</div>}

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr style={S.thead}>
              <SortTh k="date" label="Failed At" sort={sort} onSort={onSort} style={S.th} />
              {showTeam && <SortTh k="team" label="Team" sort={sort} onSort={onSort} style={S.th} />}
              <SortTh k="from" label="From (Sender)" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="to" label="To (Our Line)" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="pages" label="Pages" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="reason" label="Reason" sort={sort} onSort={onSort} style={S.th} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={S.emptyCell}><LoadingSpinner /></td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={6} style={S.emptyCell}>
                <EmptyState icon="📥" filtered={filtersDiffer(applied, FAILED_INCOMING_DEFAULTS)} onClear={resetFilters}
                  filteredText="No failed received faxes match these filters."
                  emptyText="No failed received faxes yet." />
              </td></tr>
            ) : pageRows.map((r, i) => (
              <tr key={r.faxId} style={{ ...S.tr, background: i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)' }}>
                <td style={{ ...S.td, fontSize: 12, color: 'var(--t-546e7a)', whiteSpace: 'nowrap' }}>{formatDate(r.failedAt)}
                  {r.accountName && <div style={{ fontSize: 11, color: 'var(--t-78909c)' }}>{r.accountName}</div>}</td>
                {showTeam && <td style={S.td}>{teamText(r) || '—'}</td>}
                <td style={{ ...S.td, fontWeight: 500, whiteSpace: 'nowrap' }}>{formatFaxNum(r.fromNumber)}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{formatFaxNum(r.toNumber)}</td>
                <td style={S.td}>{r.pages ?? 0}</td>
                {/* 3.62.23 (owner): in words from our side; the carrier's code on hover */}
                <td style={S.td} title={r.failureReason || ''}>{incomingReasonText(r.failureReason)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && <Pager {...pager} label="faxes" />}
    </div>
  );
}

// 3.40.1: the portal's shared loading look (components/Loader)
function LoadingSpinner() {
  return <Loader label="Loading…" />;
}

const S = {
  resendBtn: { padding: '4px 10px', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', border: '1.5px solid var(--c-b2dfdb)', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  header      : { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  pageTitle   : { margin: '0 0 4px', color: 'var(--t-00695c)', fontSize: 22, fontWeight: 700 },
  pageSubtitle: { margin: 0, color: 'var(--t-78909c)', fontSize: 14 },
  subTabs     : { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--c-e0e0e0)', paddingBottom: 0 },
  cards       : { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14, marginBottom: 16 },
  card        : { background: 'var(--c-ffffff)', borderRadius: 12, padding: '16px 20px', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--c-e0f2f1)', textAlign: 'center' },
  cardValue   : { fontSize: 26, fontWeight: 700, color: 'var(--t-00695c)', marginBottom: 4 },
  cardLabel   : { fontSize: 12, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  effectStrip : { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 18, background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '12px 20px', marginBottom: 16, boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.06)', fontSize: 13, color: 'var(--t-546e7a)' },
  effectTitle : { fontWeight: 700, color: 'var(--t-00695c)', fontSize: 14 },
  effectItem  : { whiteSpace: 'nowrap' },
  chipRow     : { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 },
  chip        : { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0e0e0)', borderRadius: 20, padding: '4px 12px', fontSize: 12, color: 'var(--t-546e7a)' },
  chipCode    : { fontSize: 12, color: 'var(--t-b71c1c)' },
  filterBar   : { background: 'var(--c-ffffff)', borderRadius: 12, padding: '20px 24px', marginBottom: 16, boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.06)', border: '1px solid var(--b-e8f5e9)' },
  filterRow   : { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' },
  filterGroup : { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 },
  filterLabel : { fontSize: 12, fontWeight: 600, color: 'var(--t-546e7a)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  filterInput : { padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, color: 'var(--t-333333)', fontFamily: 'inherit' },
  downloadBtn : { padding: '9px 14px', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', border: '1.5px solid var(--c-00897b)', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
  searchBtn   : { padding: '9px 20px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
  resultCount : { fontSize: 14, color: 'var(--t-546e7a)', marginBottom: 12 },
  errorBanner : { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  tableWrap   : { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table       : { width: '100%', borderCollapse: 'collapse' },
  thead       : { background: 'var(--c-00695c)' },
  th          : { padding: '12px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr          : { borderBottom: '1px solid var(--c-f0f0f0)' },
  td          : { padding: '11px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'middle' },
  badge       : { padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
  reasonCode  : { fontSize: 12, color: 'var(--t-b71c1c)', background: 'var(--c-fce4ec)', padding: '2px 8px', borderRadius: 4 },
  emptyCell   : { textAlign: 'center', padding: '40px 0' },
};
